<template>
  <div class="container py-5 pt-3 pt-xl-5">
    <ProgressNavComponent
      v-if="signupStep > 1 && !changingInformationDisabled"
      :steps="navSteps"
      :setStep="setStep"
    />

    <!-- STEP 1: Country selection and onboarding type -->
    <div v-if="shownStep.step_name === StepName.CHOOSE_ONBOARDING">
      <CountryPickerComponent />

      <ChooseOnboardingComponent
        :addAuthorizationAgreementFlow="createAuthorizationAccountFlow"
        :addNewDeviceFlow="createNewDeviceFlow"
        :existingCompanies="existingCompanies"
        :countryLocale="'lv'"
        @selectExistingCompany="handleSelectExistingCompany"
        @resetCompanySelection="handleResetCompanySelection"
      />
    </div>

    <!-- STEP 2: Contact review (AUTH Step 2 / NEW DEVICE Step 2) -->
    <div v-if="shownStep.step_name === StepName.CONTACT_REVIEW">
      <ContactReviewConfigurationComponent
        v-if="companyIndex !== -1"
        :key="companyIndex"
        :accountData="accountData"
        :companyIndex="companyIndex"
        @handleUpdateCompany="signupStore.updateCompany"
        @updateAccountData="signupStore.updateAccount"
        @selectExistingCompany="handleSelectExistingCompany"
        @resetCompanySelection="handleResetCompanySelection"
        class="mb-4"
      />
    </div>

    <!-- NEW DEVICE Step 3: Generator review -->
    <GeneratorReviewComponent
      v-if="
        shownStep.step_name === StepName.GENERATOR_REVIEW &&
        generatorIndex !== -1
      "
      :account-data="accountData"
      :generator-index="generatorIndex"
      :company-index="companyIndex"
      @handleUpdateGenerator="signupStore.updateGenerator"
    />

    <!-- NEW DEVICE Step 4: Document upload -->
    <MultipleUploadComponent
      v-if="shownStep.step_name === StepName.MULTIPLE_UPLOAD"
      :accountData="accountData"
      :hasGenerator="hasGenerator"
      :setLoading="setLoading"
      :uploadItems="NEW_DEVICE_UPLOAD_ITEMS"
      :defaultUploadUrl="FILE_UPLOAD_URL"
      @uploadComplete="takeUpload"
      class="mb-4"
    />

    <!-- NEW DEVICE Step 5: Company info review -->
    <LVCompanyInfoReviewConfigurationComponent
      v-if="shownStep.step_name === StepName.COMPANY_INFO_REVIEW"
      :accountData="accountData"
      :companyIndex="companyIndex"
      @handleUpdateCompany="signupStore.updateCompany"
    />

    <!-- AUTH Step 3 / NEW DEVICE Step 6: Contract review -->
    <ContractReviewConfigurationComponent
      v-if="
        shownStep.step_name === StepName.CONTRACT_REVIEW && companyIndex !== -1
      "
      :accountData="accountData"
      :setLoading="setLoading"
      :addNewDevice="!addAuthorizationAgreement"
      @createContainerForStartSigningReady="storeCreateContainerForStartSigning"
      @disableChangingInformation="handleDisableChangingInformation"
      class="mb-4"
    />

    <!-- Buttons -->
    <div
      v-if="signupStep > 1 && !changingInformationDisabled"
      class="d-flex justify-content-between w-100"
    >
      <button
        class="btn btn-secondary me-2"
        :disabled="isLoading"
        @click="setStep(signupStep - 1)"
      >
        {{ $t("signup.navsteps.back") }}
      </button>

      <button
        class="btn btn-primary"
        :disabled="isLoading"
        @click="
          shownStep.step_name === StepName.CONTRACT_REVIEW
            ? createContainerForStartSigningFunction()
            : setStep(signupStep + 1)
        "
      >
        {{
          shownStep.step_name === StepName.CONTRACT_REVIEW
            ? $t("signup.navsteps.to_signature_page")
            : $t("signup.navsteps.next")
        }}
      </button>
    </div>
    <!-- Loading overlay, shown only when isLoading is true -->
    <LoadingOverlayComponent v-if="isLoading" />
    <!-- AccountDataDebugComponent for debugging -->
    <AccountDataDebugComponent
      :accountData="accountData"
      class="mb-4"
    ></AccountDataDebugComponent>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import AccountDataDebugComponent from "@/components/common/AccountDataDebugComponent.vue";
import LoadingOverlayComponent from "@/components/common/LoadingOverlayComponent.vue";
import ContactReviewConfigurationComponent from "@/signup/lv/ContactReviewConfigurationComponent.vue";
import ContractReviewConfigurationComponent from "@/signup/lv/ContractReviewConfigurationComponent.vue";
import ProgressNavComponent from "@/signup/ProgressNavComponent.vue";
import { useSignupStore } from "@/signup/signup";
import { useSignupStepSave } from "@/signup/composables/useSignupStepSave";
import { storeToRefs } from "pinia";
import { useQueryManager } from "@/composables/useQueryManager";
import { useStepFunctions, StepConfig } from "@/composables/useStepFunctions";
import CountryPickerComponent from "@/signup/CountryPickerComponent.vue";
import { useI18n } from "vue-i18n";
import MultipleUploadComponent from "@/signup/MultipleUploadComponent.vue";
import ChooseOnboardingComponent from "@/signup/ChooseOnboardingComponent.vue";
import GeneratorReviewComponent from "@/signup/lv/GeneratorReviewComponent.vue";
import LVCompanyInfoReviewConfigurationComponent from "@/signup/lv/LVCompanyInfoReviewConfigurationComponent.vue";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";

enum StepName {
  CHOOSE_ONBOARDING,
  CONTACT_REVIEW,
  MULTIPLE_UPLOAD,
  GENERATOR_REVIEW,
  CONTRACT_REVIEW,
  COMPANY_INFO_REVIEW,
}

const NEW_DEVICE_UPLOAD_ITEMS = [
  {
    fileKey: "UF_lv_grid_connection_scheme",
    required: true,
  },
  {
    fileKey: "UF_lv_document_of_device_auxiliaries",
    required: false,
  },
  {
    fileKey: "UF_lv_power_of_attorney",
    required: false,
  },
];

const FILE_UPLOAD_URL = "/api/lv/upload-generator-file";

const createContainerForStartSigningFunction = ref<() => void | Promise<void>>(
  () => undefined
);

const isLoading = ref(true);
const { globalErrorHandler } = useGlobalHandlers();
const signupStore = useSignupStore();
const {
  account: accountData,
  companyIndex,
  generatorIndex,
} = storeToRefs(signupStore);
const { updateQuery, getQueryValue, parsers } = useQueryManager();
const signupStep = getQueryValue("step", (value) =>
  value ? parseInt(value, 10) : 1
);
const addAuthorizationAgreement = getQueryValue(
  "addAuthorizationAgreement",
  parsers.boolean
);

const { postStepCallback, takeUpload } = useSignupStepSave(isLoading);
const { t } = useI18n();

const existingCompanies = computed(() => {
  return accountData.value.companies.filter(
    (company) =>
      company.id !== undefined &&
      company.country === "LV" &&
      company.documents.length > 0
  );
});

const hasGenerator = computed(() => {
  return generatorIndex.value !== -1;
});

const STEP_CONFIG: Record<StepName, StepConfig> = {
  [StepName.CHOOSE_ONBOARDING]: {
    step_name: StepName.CHOOSE_ONBOARDING,
    title: t("signup.navsteps.lv.step1"),
    titleKey: "signup.navsteps.lv.step1",
  },
  [StepName.CONTACT_REVIEW]: {
    step_name: StepName.CONTACT_REVIEW,
    title: t("signup.navsteps.lv.step2"),
    titleKey: "signup.navsteps.lv.step2",
    apiEndpoint: "/api/lv/update-company",
    postStepCallback: postStepCallback,
    preStepCallback: async (_: StepConfig) => {
      signupStore.createCompanyIfMissing();
      return true;
    },
  },
  [StepName.MULTIPLE_UPLOAD]: {
    step_name: StepName.MULTIPLE_UPLOAD,
    title: t("signup.navsteps.lv.new_device.3"),
    titleKey: "signup.navsteps.lv.new_device.3",
    postStepCallback: postStepCallback,
  },
  [StepName.GENERATOR_REVIEW]: {
    step_name: StepName.GENERATOR_REVIEW,
    title: t("signup.navsteps.lv.new_device.4"),
    titleKey: "signup.navsteps.lv.new_device.4",
    apiEndpoint: "/api/lv/update-generator",
    postStepCallback: postStepCallback,
    preStepCallback: async (_: StepConfig) => {
      if (!hasGenerator.value) {
        signupStore.createGeneratorIfMissing();
      }
      return true;
    },
  },
  [StepName.COMPANY_INFO_REVIEW]: {
    step_name: StepName.COMPANY_INFO_REVIEW,
    title: t("signup.navsteps.lv.new_device.5"),
    titleKey: "signup.navsteps.lv.new_device.5",
    apiEndpoint: "/api/lv/update-company",
    postStepCallback: postStepCallback,
  },
  [StepName.CONTRACT_REVIEW]: {
    step_name: StepName.CONTRACT_REVIEW,
    title: t("signup.navsteps.lv.new_device.6"),
    titleKey: "signup.navsteps.lv.new_device.6",
    postStepCallback: postStepCallback,
  },
};

const flowSteps = computed(() => {
  if (addAuthorizationAgreement.value) {
    return [
      STEP_CONFIG[StepName.CHOOSE_ONBOARDING],
      STEP_CONFIG[StepName.CONTACT_REVIEW],
      STEP_CONFIG[StepName.CONTRACT_REVIEW],
    ];
  } else {
    return [
      STEP_CONFIG[StepName.CHOOSE_ONBOARDING],
      STEP_CONFIG[StepName.CONTACT_REVIEW],
      STEP_CONFIG[StepName.GENERATOR_REVIEW],
      STEP_CONFIG[StepName.MULTIPLE_UPLOAD],
      STEP_CONFIG[StepName.COMPANY_INFO_REVIEW],
      STEP_CONFIG[StepName.CONTRACT_REVIEW],
    ];
  }
});

const { shownStep, setStep, navSteps, invokePreStepCallback } =
  useStepFunctions(flowSteps, signupStep);

const setLoading = (value: boolean) => {
  isLoading.value = value;
};

const storeCreateContainerForStartSigning = (
  createContainerForStartSigning: () => Promise<void>
) => {
  createContainerForStartSigningFunction.value = createContainerForStartSigning;
};

const changingInformationDisabled = ref(false);

const handleDisableChangingInformation = () => {
  changingInformationDisabled.value = true;
};

const createAuthorizationAccountFlow = async () => {
  await updateQuery({ addAuthorizationAgreement: "true" });
  setStep(2);
};

const createNewDeviceFlow = async () => {
  await updateQuery({ addAuthorizationAgreement: "false" });
  setStep(2);
};

const handleSelectExistingCompany = async (companyId: number) => {
  isLoading.value = true;
  try {
    await updateQuery({
      company_id: companyId.toString(),
      step: "2",
    });
    signupStore.removeEmptyCompanyIfExists();
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    isLoading.value = false;
  }
};

const handleResetCompanySelection = () => {
  updateQuery({
    company_id: undefined,
    generator_id: undefined,
    step: "1",
  });
  signupStore.removeEmptyCompanyIfExists();
};

onMounted(async () => {
  await invokePreStepCallback();
  isLoading.value = false;
});
</script>
